import { Person } from './person';
import { Status } from './status';
import { Task } from './task';
import { Attachable } from './attachable';
import { Issue } from './issue';
import { Comment as IssueComment } from './comment';
import { CriticalLevel } from './enums/criticalLevel';
import { IssueType } from './enums/issueType';
import { Sticker } from './sticker';
import { IssueTypeMappingUtil } from '../util/issuetype-mapping-util';
import { IssueLink } from './issueLink';

export class Userstory implements Issue {
  private disabled: boolean; // This is used for disabling / enabling dragging with cdkDragDisabled
  public assignee: Person;
  public description: string;
  public estimate: number;
  public id: number;
  public parentIssueId: number;
  public key: string;
  public summary: string;
  public reporter: Person;
  public status: Status;
  public tasks: Task[];
  public attachments: Attachable[];
  public rank: string;
  public storyPoints: number;
  public isSubissue = false;
  public issueType: IssueType;
  public comments: IssueComment[] = [];
  public changelog: string[];
  public created: string;
  public criticalLevel: CriticalLevel;
  public isHighlighted: boolean;
  public isVisible: boolean;
  public stickers: Sticker[] = [];
  public isExpedited: boolean;
  public issueLinks: IssueLink[] = [];
  public updatedAt: string;
  public lastViewed: string;
  public lastViewedInAwall: string;

  constructor(assignee: Person,
    description: string,
    estimate: number,
    id: number,
    parentIssueId: number,
    key: string,
    summary: string,
    issueType: IssueType,
    reporter: Person,
    state: Status,
    tasks: Task[],
    attachments: Attachable[],
    storyPoints: number,
    changelog: string[],
    created: string,
    isExpedited: boolean,
    updatedAt: string,
    lastViewed: string,
    lastViewedInAwall: string,
    issueLinks: IssueLink[]) {
    this.assignee = assignee;
    this.description = description;
    this.estimate = estimate;
    this.id = Number(id);
    // For an issue with no parentIssue, this is the id of the issue itself again!
    this.parentIssueId = Number(id);
    this.key = key;
    this.summary = summary;
    this.issueType = issueType;
    this.reporter = reporter;
    this.status = new Status(state.description, state.iconUrl, state.id, state.name, state.statusCategory);
    this.storyPoints = storyPoints;
    this.tasks = [];
    this.isExpedited = isExpedited;
    this.updatedAt = updatedAt;
    this.lastViewed = lastViewed;
    this.lastViewedInAwall = lastViewedInAwall;
    this.issueLinks = issueLinks;
    tasks.forEach(singleTask => {
      this.tasks.push(
        new Task(
          singleTask.id,
          singleTask.summary,
          singleTask.description,
          singleTask.estimate,
          singleTask.time_spent,
          singleTask.assignee,
          singleTask.reporter,
          singleTask.status,
          singleTask.parentIssueId,
          singleTask.attachments,
          singleTask.changelog,
          singleTask.created,
          singleTask.isExpedited,
          singleTask.issueLinks,
          singleTask.updatedAt,
          singleTask.lastViewed,
          singleTask.lastViewedInAwall));
    });
    this.attachments = attachments;
    this.changelog = changelog;
    this.created = created;
    this.criticalLevel = CriticalLevel.None;
    this.isVisible = true;
  }

  public update(other: Userstory) {
    this.assignee = other.assignee;
    this.description = other.description;
    this.estimate = other.estimate;
    this.id = other.id;
    this.key = other.key;
    this.summary = other.summary;
    this.issueType = other.issueType;
    this.reporter = other.reporter;
    this.status = other.status;
    this.tasks = other.tasks;
    this.attachments = other.attachments;
    this.storyPoints = other.storyPoints;
    // TODO check if update happen with the same task (if not changelog must be consistence)
    this.changelog = other.changelog;
    this.created = other.created;
    this.isVisible = other.isVisible;
    this.isHighlighted = other.isHighlighted;
    this.criticalLevel = other.criticalLevel;
    this.isExpedited = other.isExpedited;
    this.issueLinks = other.issueLinks;
    this.updatedAt = other.updatedAt;
    this.lastViewed = other.lastViewed;
    this.lastViewedInAwall = other.lastViewedInAwall;
  }

  public getCopy(): Userstory {
    const copy: Userstory = new Userstory(this.assignee, this.description, this.estimate,
      this.id, this.parentIssueId, this.key, this.summary, this.issueType, this.reporter,
      this.status, this.tasks, this.attachments, this.storyPoints, this.changelog, this.created,
      this.isExpedited, this.updatedAt, this.lastViewed, this.lastViewedInAwall, this.issueLinks);
    copy.isVisible = this.isVisible;
    copy.isHighlighted = this.isHighlighted;
    copy.criticalLevel = this.criticalLevel;
    return copy;
  }

  disable(): void {
    this.disabled = true;
  }

  enable(): void {
    this.disabled = false;
  }

  isDisabled() {
    return this.disabled;
  }

  isCritical() {
    return this.criticalLevel === CriticalLevel.Critical;
  }
  isWarning() {
    return this.criticalLevel === CriticalLevel.Warning;
  }
}

/**
 * the PyWall needs a different formatting from us that he gives us
 * this class maps the data to a format the PyWall expects
 */
export class UpdateUserstory {
  id: number;
  summary: string;
  description: string;
  issuetype: string;
  reporter: object; // reporter.id
  assignee: object; // assignee.id
  status: object;
  estimate: string;
  attachments: number[];
  storyPoints: number;
  changelog: string[];
  created: string;
  isExpedited: boolean;
  updatedAt: string;
  lastViewed: string;
  last_viewed_in_awall: string;

  constructor(userstory: Userstory) {
    this.id = userstory.id;
    this.summary = userstory.summary;
    this.description = userstory.description;
    this.issuetype = IssueTypeMappingUtil.mapToJiraIssueTypeName(userstory.issueType);
    this.assignee = (userstory.assignee) ? {'id': userstory.assignee.id} : null;
    this.reporter = (userstory.reporter) ? {'id': userstory.reporter.id} : null;
    this.status = userstory.status;
    this.estimate = '';
    this.attachments = userstory.attachments.map(a => a.id);
    this.storyPoints = userstory.storyPoints;
    this.changelog = [];
    this.created = userstory.created;
    this.isExpedited = userstory.isExpedited;
    this.updatedAt = userstory.updatedAt;
    this.lastViewed = userstory.lastViewed;
    this.last_viewed_in_awall = userstory.lastViewedInAwall;
  }
}
